import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import LinkButtons from '../../common/input/LinkButtons';
import CsrOptions from '../csrOptions/CsrOptions';
import { useShowEligibilityCriteria } from '../../hooks/useShowEligibilityCriteria';
import { useIsUserAllowedForActions } from '../../hooks/useIsUserAllowedForActions';
import { useIsActionsButtonAccessible } from '../../hooks/useIsActionsButtonAccessible';
import { COVERAGE_YEAR_DISPLAY_COLUMN, ELIGIBILITY_WORKSHEET_DISPLAY_COLUMN } from '../../utils';
import {
    ConditionalRendering,
    useAppSelector,
    configs,
    formatAptc,
    IApplication,
    AccountTransferStatus,
} from '../../utils';

import {
    StyledContainer,
    StyledList,
    StyledListItem,
    StyledLabel,
    StyledValue,
    StyledMainContainer,
    StyledCardContainer,
    StyledActionsContainer,
    StyledEventValue,
    StyleSsapApplicantEvent,
    StyledCoverage,
} from './ApplicationsListCard.styles';
import { setSsapLaunch } from '../../../../actions/ssap/ssapActionCreators';

const ApplicationsListCard = ({ application, applicationIndex, applicationListId}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isState = useSelector((state: any) => state?.common?.environment?.isState);
    const outBoundButtonStatus = useAppSelector(state => state.capApplications.outBoundButtonStatus);
    const { myAppOveedApplnLock, myAppEndSEP } = useAppSelector(state => state.capApplications.appControllerInfo);
    const [showCsrOptions, setShowCsrOptions] = useState(false);
    const isUserAllowedForActions = useIsUserAllowedForActions(application, applicationIndex);
    const isActionsButtonAccessible = useIsActionsButtonAccessible(application);
    const { showApplicationSummary, showEligibilityWorkSheet } = useShowEligibilityCriteria(application);
    const showActionsButton =
        (application.isApplicationCurrent && isUserAllowedForActions) ||
        (!application.isApplicationCurrent && isActionsButtonAccessible);
    const showEligibilityWorksheetLink = columnIndex => {
        return columnIndex === ELIGIBILITY_WORKSHEET_DISPLAY_COLUMN && showEligibilityWorkSheet;
    };
    const showApplicationSummaryLink = columnIndex => {
        return columnIndex === ELIGIBILITY_WORKSHEET_DISPLAY_COLUMN && showApplicationSummary;
    };

    const cardColumn1 = [
        { id: 'cap.applications.column.type', value: application.appType, visibility: true },
        {
            id: 'cap.applications.column.primaryTaxFiler',
            value: `${application.ptfFirstName ?? ''} ${application.ptfMiddleName ?? ''} ${application.ptfLastName ?? ''}`,
            visibility: true,
        },
        { id: 'cap.applications.column.appStatus', value: application.applicationStatus, visibility: true },
        {
            id: 'cap.applications.column.dentalAppStatus',
            value:
                application.applicationDentalStatus === '' ? (
                    <FormattedMessage
                        id="cap.applications.message.notAvailable"
                        data-testid="cap-applications-column-dentalAppStatus-value"
                    />
                ) : (
                    application.applicationDentalStatus
                ),
            visibility: true,
        },
    ];

    const cardColumn2 = [
        {
            id: 'cap.applications.column.eligibilityStartDate',
            value: application.eligibilityStartDate,
            visibility: true,
        },
        { id: 'cap.applications.column.eligibilityEndDate', value: application.eligibilityEndDate, visibility: true },
        {
            id: 'cap.applications.column.qhp',
            value:
                application.isExchangeEligible ? (
                    <FormattedMessage id="label.yes" data-testid="cap-applications-column-qhp-value" />
                ) : (
                    <FormattedMessage id="label.no" data-testid="cap-applications-column-qhp-value" />
                ),
            visibility: true,
        },
        {
            id: 'cap.applications.column.aptc',
            value:
                application.aptc === null ? (
                    <FormattedMessage
                        id="cap.applications.message.notEligible"
                        data-testid="cap-applications-column-aptc-value"
                    />
                ) : (
                    formatAptc(application.aptc)
                ),
            visibility: true,
        },
        {
            id: 'cap.applications.column.stateSubsidy',
            value:
                application.stateSubsidy === null ? (
                    <FormattedMessage
                        id="cap.applications.message.notEligible"
                        data-testid="cap-applications-column-stateSubsidy-value"
                    />
                ) : (
                    application.stateSubsidy
                ),
            visibility: application.stateSubsidyEnabled,
        },
        {
            id: 'cap.applications.column.csr',
            value:
                application.csr === null ? (
                    <FormattedMessage
                        id="cap.applications.message.notEligible"
                        data-testid="cap-applications-column-csr-value"
                    />
                ) : (
                    application.csr
                ),
            visibility: true,
        },
    ];

    const cardColumn3 = [
        { id: 'cap.applications.column.caseNumber', value: application.caseNumber, visibility: true },
        { id: 'cap.applications.column.creationDate', value: application.createdDate, visibility: true },
        { id: 'cap.applications.column.lastUpdated', value: application.lastUpdated, visibility: true },
        {
            id: 'cap.applications.column.accountTransferProcessDate',
            value: application.accountTransferProcessDate,
            visibility:
                application.accountTransferStatus === AccountTransferStatus.DETERMINED &&
                application.accountTransferProcessDate,
        },
        {
            id: 'cap.applications.column.medicaidOutboundAssessed',
            value: application.accountTransferProcessDate,
            visibility:
                application.accountTransferStatus === AccountTransferStatus.ASSESSED &&
                application.accountTransferProcessDate,
        },
        {
            id: 'cap.applications.column.medicaidOutboundReferral',
            value: application.accountTransferProcessDate,
            visibility:
                application.accountTransferStatus === AccountTransferStatus.REFERRAL &&
                application.accountTransferProcessDate,
        },
        {
            id: 'cap.applications.column.medicaidOutboundReceived',
            value: application.accountTransferProcessDate,
            visibility:
                application.accountTransferStatus === AccountTransferStatus.RECEIVED &&
                application.accountTransferProcessDate,
        },
        {
            id: 'cap.applications.column.processingstatus',
            value: application.queuedStatus,
            visibility: application.queuedStatus,
        },
        {
            id: 'cap.applications.column.spansequence',
            value: `${application.currentSpan} of ${application.totalSpan}`,
            visibility: application.currentSpan,
        },
        {
            id: 'cap.applications.column.expectedprocessingdate',
            value: 'dueDate',
            visibility: application.dueDate,
        },
    ];

    const onClickActionsViewHandler = () => {
        setShowCsrOptions(!showCsrOptions);
    };

    const onClickViewAppEligibility = (application: IApplication) => {
        let applicationObject = {};
        applicationObject['coverageYear'] = application.coverageYear;
        applicationObject['caseNumber'] = application.caseNumber;
        localStorage.copySessionData = JSON.stringify(applicationObject);
        navigate(configs.routes.CRM_APPLICATION_ELIGIBILITY);
    };

    const onClickViewAppSummary = (application: IApplication) => {
        const encHouseHoldId = sessionStorage.getItem('encHouseHoldId') ?? 'encID';
        dispatch(setSsapLaunch({
            backLinkText: 'common.backToApplication',
            backUrl: configs.routes.CAP_APPLICATIONS.replace(':id', encHouseHoldId),
            caseNumber: application.caseNumber,
            consumer: 'CAP',
            loadBy: 'caseNumber',
            watchForDuplications: false
        }));
        navigate(`${configs.routes.CAP_APPLICATIONS_SSAP_SUMMARY.replace(':id', encHouseHoldId)}`);
    };

    const appInfoRender = [cardColumn1, cardColumn2, cardColumn3];

    const renderColumn = (column, columnIndex: number) => (
        <StyledList key={`column-${columnIndex}`} data-testid={`column-${columnIndex}`}>
            <ConditionalRendering isTrue={isState?.ID && columnIndex === COVERAGE_YEAR_DISPLAY_COLUMN}>
                <StyledCoverage data-testid="application-coverage">
                    {application.coverageYear} {application.applicationName}
                </StyledCoverage>
            </ConditionalRendering>

            <ConditionalRendering isTrue={!isState?.ID && columnIndex === COVERAGE_YEAR_DISPLAY_COLUMN}>
                <StyledCoverage data-testid="application-coverage">
                    {application.coverageYear} <FormattedMessage id={'cap.applications.column.coverage'} />
                </StyledCoverage>
            </ConditionalRendering>

            {column.map(({ id, value, visibility }) => (
                <ConditionalRendering 
                    isTrue={visibility} 
                    key={`listItem_${id}`}
                >
                    <StyledListItem
                        key={`listItem-${id}`}
                        data-testid={`${applicationListId + '-' + id.replaceAll('.', '-')}-${applicationIndex}-li`}
                    >
                        <StyledLabel
                            data-testid={`${applicationListId + '-' + id.replaceAll('.', '-')}-${applicationIndex}`}
                        >
                            <FormattedMessage id={id} />
                        </StyledLabel>
                        :{' '}
                        <StyledValue data-testid={`${id.replaceAll('.', '-')}-value-${applicationIndex}`}>
                            {value}
                        </StyledValue>
                    </StyledListItem>
                </ConditionalRendering>
            ))}
            <ConditionalRendering isTrue={showEligibilityWorksheetLink(columnIndex)}>
                <LinkButtons
                    id="view_eligibility_worksheet"
                    labelId={'cap.applications.btn.viewEligWorksheet'}
                    variant="primary"
                    iconPlacement="start"
                    onClickHandler={() => onClickViewAppEligibility(application)}
                    data-automation-id="view_eligibility_worksheet"
                />
            </ConditionalRendering>
            <ConditionalRendering isTrue={showApplicationSummaryLink(columnIndex)}>
                <StyledListItem className='mt-3'>
                    <LinkButtons
                        id="view_application_summary"
                        labelId={'cap.applications.btn.viewApplicationSummary'}
                        variant="primary"
                        onClickHandler={() => onClickViewAppSummary(application)}
                        data-automation-id="view_application_summary"
                    />
                </StyledListItem>
            </ConditionalRendering>
        </StyledList>
    );

    return (
        <StyledMainContainer>
            <StyledContainer>{appInfoRender.map((column, index) => renderColumn(column, index))}</StyledContainer>

            {application.ssapApplicantEvents.length > 0 && (
                <StyleSsapApplicantEvent>
                    <FormattedMessage id="cap.applications.eventDate" />
                    {application.ssapApplicantEvents.map((event, index) => (
                        <StyledEventValue className="span-font" key={event.eventDate} data-testid={`event-${index}`}>
                            {event.ssapApplicant} - {event.sepEvents}, {dayjs(event.eventDate).format('YYYY-MM-DD')}
                        </StyledEventValue>
                    ))}
                </StyleSsapApplicantEvent>
            )}

            <ConditionalRendering isTrue={showActionsButton}>
                <StyledActionsContainer className="btn-actions">
                    <LinkButtons
                        id="member_view"
                        labelId={'cap.applications.btn.actions'}
                        variant="primary"
                        icon={<FontAwesomeIcon icon="cog" title="faCog" />}
                        iconPlacement="start"
                        onClickHandler={onClickActionsViewHandler}
                    />
                </StyledActionsContainer>
            </ConditionalRendering>

            <ConditionalRendering isTrue={showCsrOptions}>
                <StyledCardContainer>
                    <CsrOptions
                        application={application}
                        applicationIndex={applicationIndex}
                        outBoundButtonStatus={outBoundButtonStatus}
                        myappOveedApplnLockJs={myAppOveedApplnLock}
                        myAppEndSEPJs={myAppEndSEP}
                    />
                </StyledCardContainer>
            </ConditionalRendering>
        </StyledMainContainer>
    );
};

export default ApplicationsListCard;
